import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, FileText, Percent, Globe } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What should a Bangladeshi invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A Bangladeshi invoice should include: your business name and address; your BIN (Business Identification Number) if VAT-registered; the client name and address; a unique invoice number; the invoice date; a description of goods or services; the amount; and your payment details. VAT-registered businesses must also show the applicable VAT rate and VAT amount separately.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the VAT rate in Bangladesh?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The standard VAT rate in Bangladesh is 15%, which applies to most goods and services. Some goods and services attract reduced rates of 5% or 10%. VAT registration is required for businesses with annual turnover exceeding BDT 30 lakh (3,000,000). Exports of goods and services are zero-rated at 0% VAT.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do Bangladeshi freelancers invoice international clients?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Bangladeshi freelancers providing services to overseas clients typically invoice in USD. Export of services to non-residents is zero-rated — no VAT is added. Use the international invoice type to bill in USD, GBP, or EUR with your bank account details. Most Bangladeshi freelancers receive payments via Payoneer, Wise, or local bank remittances.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is BIN and when do freelancers in Bangladesh need it?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'BIN (Business Identification Number) is the VAT registration number issued by the National Board of Revenue (NBR) in Bangladesh. You need a BIN if your annual turnover exceeds BDT 30 lakh and you supply VAT-taxable goods or services. Freelancers exporting services to foreign clients are generally not required to charge VAT regardless of turnover.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this invoice generator free for Bangladeshi users?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic is completely free for Bangladeshi freelancers and businesses. Create professional invoices in USD, BDT, or any currency. Download a clean PDF instantly — no sign-up required for up to 5 invoices per month.',
      },
    },
  ],
};

const invoiceTypes = [
  {
    icon: Globe,
    title: 'International Invoice',
    subtitle: 'For billing overseas clients in USD',
    features: ['USD, GBP, EUR billing', 'Zero-rated export format', 'SWIFT / bank details', 'Multi-currency support', 'Professional PDF'],
    to: '/create/international',
    color: 'green',
    btnClass: 'bg-green-600 hover:bg-green-700',
  },
  {
    icon: FileText,
    title: 'Standard Invoice',
    subtitle: 'For local clients in BDT',
    features: ['BDT currency', 'Custom line items', 'Logo upload', 'BIN number field', 'Payment terms'],
    to: '/create/freelancer',
    color: 'blue',
    btnClass: 'bg-blue-600 hover:bg-blue-700',
  },
  {
    icon: Percent,
    title: 'VAT Invoice',
    subtitle: 'For VAT-registered businesses',
    features: ['15% VAT auto-calculated', 'BIN number field', 'Net + VAT + total', 'NBR-ready format', 'BDT currency'],
    to: '/create/vat',
    color: 'indigo',
    btnClass: 'bg-indigo-600 hover:bg-indigo-700',
  },
];

export default function InvoiceGeneratorBangladeshPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Invoice Generator Bangladesh: Free BDT Invoice — NBR, 15% VAT & Payoneer | Invilic"
        description="Free invoice generator for Bangladeshi freelancers. BDT and USD invoices, 15% VAT auto-calc, BIN/NBR fields, Payoneer & bKash payment. PDF download free, no sign-up."
        canonical="https://www.invilic.com/invoice-generator-bangladesh"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <span className="inline-block bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            🇧🇩 Bangladesh
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Invoice Generator Bangladesh
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Free invoice generator for Bangladeshi freelancers and businesses. Create professional invoices in USD, BDT, or any currency. Bill international clients with zero-rated export invoices or local clients in BDT. Download a clean PDF in seconds.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/create/international" className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-green-600/20 transition-all duration-200">
              Bill International Clients <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/create/freelancer" className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200">
              Create BDT Invoice <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">Which Invoice Do You Need?</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {invoiceTypes.map(({ icon: Icon, title, subtitle, features, to, color, btnClass }) => (
              <div key={title} className={`border border-${color}-100 dark:border-${color}-800/30 rounded-2xl p-6 bg-${color}-50 dark:bg-${color}-900/10`}>
                <div className={`w-10 h-10 rounded-xl bg-${color}-100 dark:bg-${color}-900/40 flex items-center justify-center mb-4`}>
                  <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">{title}</h3>
                <p className={`text-xs text-${color}-700 dark:text-${color}-300 mb-4`}>{subtitle}</p>
                <ul className="space-y-2 mb-5">
                  {features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <CheckCircle className={`w-3.5 h-3.5 text-${color}-500 flex-shrink-0`} /> {f}
                    </li>
                  ))}
                </ul>
                <Link to={to} className={`inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 ${btnClass} text-white text-sm font-semibold rounded-xl transition-all`}>
                  Create Free <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16 p-8 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-5">International vs Local Invoice: Which Do You Need?</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <div className="font-semibold text-green-700 dark:text-green-400 mb-3">Use an International Invoice if:</div>
              {[
                'Your client is overseas (US, UK, EU, Middle East, etc.)',
                'You bill in USD, GBP, EUR, or another foreign currency',
                'Your services are zero-rated for export — no 15% VAT applies',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
            <div>
              <div className="font-semibold text-blue-700 dark:text-blue-400 mb-3">Use a BDT Invoice if:</div>
              {[
                'Your client is based in Bangladesh and pays in BDT',
                'You provide services to local businesses or individuals',
                'You need to include BIN and local business details',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* NBR VAT + payment methods */}
        <section className="mb-16 grid sm:grid-cols-2 gap-6">
          <div className="p-6 rounded-2xl border border-indigo-100 dark:border-indigo-800/40 bg-indigo-50 dark:bg-indigo-900/20">
            <h2 className="font-bold text-gray-900 dark:text-white mb-3">15% VAT & NBR Registration</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Bangladesh VAT is administered by the National Board of Revenue (NBR). Key rules:
            </p>
            <ul className="space-y-2">
              {[
                'Standard VAT rate: 15% on most goods and services',
                'Registration required above BDT 30 lakh annual turnover',
                'Add BIN (Business Identification Number) to VAT invoices',
                'Export of services to foreign clients: zero-rated (0% VAT)',
              ].map(item => (
                <li key={item} className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <CheckCircle className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="p-6 rounded-2xl border border-green-100 dark:border-green-800/40 bg-green-50 dark:bg-green-900/20">
            <h2 className="font-bold text-gray-900 dark:text-white mb-3">Receiving International Payments</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Popular options for Bangladeshi freelancers to receive USD payments:
            </p>
            <ul className="space-y-2">
              {[
                'Payoneer — withdraw to any Bangladeshi bank',
                'Wise — competitive rates, reliable transfers',
                'Bank wire (SWIFT) — direct to remittance account',
                'bKash / Nagad — for local BDT payments only',
              ].map(item => (
                <li key={item} className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Bangladesh Invoice Generator: Frequently Asked Questions</h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Create Your Bangladeshi Invoice for Free</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF in USD, BDT, or any currency. No account required.</p>
          <Link to="/create/international" className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-green-600/20 transition-all duration-200 text-lg">
            Create Invoice Free <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
    </div>
  );
}
